import axios, { AxiosError, type AxiosInstance } from "axios";
import type { ApiError } from "@/types/api";

const billingBaseURL =
  import.meta.env.VITE_BILLING_API_BASE_URL ??
  "https://ba-dev-cr-billing-service-ew1-410744466229.europe-west1.run.app/billing";

export interface BillingPrice {
  recurring: {
    interval: "monthly" | "yearly" | string;
  } | null;
  unitAmount: number;
  currency: string;
}

export interface ProductFeature {
  id: number;
  active: boolean;
  features: {
    id: number;
    featureName: string;
    iconUrl: string | null;
    unit: string | null;
    quota: number | null;
    active: boolean;
    boolean: boolean;
  };
}

export interface ProductConfig {
  id: number;
  displayOrder: number;
  extProductId: string;
  name: string;
  description: string;
  tokensCount: number | null;
  priceDetails: Record<string, BillingPrice>;
  type: string;
  subType: string;
  productFeatures: ProductFeature[];
}

export interface SubscriptionDetails {
  amount: number;
  currency: string;
  interval: string;
  productName: string;
  productDescription: string;
}

export interface SubscriptionFeature {
  id: number;
  availableQuota: number | null;
  productFeature: ProductFeature;
  baseQuota?: number;
  addonQuota?: number;
  usedQuota?: number;
}

export interface SubscriptionData {
  extSubscriptionId: string;
  agencyId: string;
  subscriptionStatus: string;
  subscriptionType: string;
  subscriptionDetails: SubscriptionDetails;
  subscriptionFeatures: SubscriptionFeature[];
  subscriptionStart: string | null;
  subscriptionEnd: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  pendingPlanId: number | null;
  stripeScheduleId: string | null;
  priceId: string | null;
  productId: string | null;
  latestInvoiceId?: string | null;
}

export interface BillingErrorResponse {
  errors: { code: string; message: string }[];
}

export interface PaymentDetails {
  id: string;
  number: string;
  status: string;
  currency: string;
  total: number;
  customer_name: string;
  customer_email: string;
  hosted_invoice_url: string;
  invoice_pdf: string;
  payment_settings?: {
    payment_method_types?: string[] | null;
  };
  customer_address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    postal_code?: string | null;
  };
}

export interface PaymentTransaction {
  agencyId: string;
  extInvoiceId: string;
  id: number;
  paymentDetails: PaymentDetails;
  paymentStatus: string;
  planName: string;
}

interface InvoicePageResponse {
  content?: PaymentTransaction[];
  _embedded?: {
    invoiceDtoList?: PaymentTransaction[];
    invoices?: PaymentTransaction[];
    content?: PaymentTransaction[];
  };
  page?: {
    content?: PaymentTransaction[];
  };
}

export interface PaymentLinkPayload {
  agencyId: string;
  products: {
    list: {
      priceId: string;
      quantity: number;
    }[];
  };
}

export interface UpgradePreview {
  amountDue: number | null;
  amountDueFormatted: string | null;
  currency: string | null;
  subtotal: number | null;
  discountAmount: number | null;
  taxAmount: number | null;
  total: number | null;
  currentDiscountLabel: string | null;
  currentPlanAmount: number | null;
  newPlanAmount: number | null;
  couponValid: boolean;
  couponMessage: string | null;
  currentPlanName: string | null;
  currentPlanCycle: string | null;
  newPlanName: string | null;
  newPlanCycle: string | null;
  scenarioSummary: string | null;
}

export interface FreemiumSubscriptionPayload {
  branchId?: string | null;
  branchName?: string | null;
  branchEmail?: string | null;
  branchPhone?: string | null;
  legalEntityId?: string | null;
  legalEntityName?: string | null;
  legalEntityEmail?: string | null;
  legalEntityPhone?: string | null;
  town?: string | null;
  postcode?: string | null;
}

export interface BillingAgencyDetails {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
}

export interface BillingAgencyAccess {
  agencyId: string;
  subscriptionManagementEnabled: boolean;
  configured: boolean;
}

export const billingClient: AxiosInstance = axios.create({
  baseURL: billingBaseURL,
  timeout: 30_000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

console.log("[billing] API base URL", billingBaseURL);

billingClient.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase() ?? "GET";
  const url = `${config.baseURL ?? billingBaseURL}${config.url ?? ""}`;
  const headers = (
    typeof config.headers?.toJSON === "function"
      ? config.headers.toJSON()
      : config.headers
  ) as Record<string, unknown> | undefined;
  console.log("[billing] request", method, url, {
    params: config.params,
    agencyId: headers?.["X-Agency-Id"] ?? headers?.["x-agency-id"],
    headers,
  });
  return config;
});

billingClient.interceptors.response.use(
  (response) => {
    const method = response.config.method?.toUpperCase() ?? "GET";
    const url = `${response.config.baseURL ?? billingBaseURL}${response.config.url ?? ""}`;
    console.log("[billing] response", response.status, method, url);
    return response;
  },
  (error: AxiosError<ApiError | BillingErrorResponse>) => {
    const method = error.config?.method?.toUpperCase() ?? "GET";
    const url = `${error.config?.baseURL ?? billingBaseURL}${error.config?.url ?? ""}`;
    console.log("[billing] error", method, url, {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
    });
    return Promise.reject(error.response?.data ?? error);
  },
);

function agencyHeaders(agencyId: string, details?: BillingAgencyDetails) {
  if (!agencyId?.trim()) {
    throw new Error("Billing request blocked: billing Legal Entity id is missing.");
  }
  return {
    "X-Agency-Id": agencyId,
    ...(details?.name?.trim() ? { "X-Agency-Name": details.name.trim() } : {}),
    ...(details?.email?.trim() ? { "X-Agency-Email": details.email.trim() } : {}),
    ...(details?.phone?.trim() ? { "X-Agency-Phone": details.phone.trim() } : {}),
  };
}

export const billingService = {
  getProducts: async (agencyId: string): Promise<ProductConfig[]> => {
    console.log("[billing] getProducts", { agencyId });
    const { data } = await billingClient.get<{ response: ProductConfig[] }>("/product/config", {
      headers: agencyHeaders(agencyId),
    });
    return data.response ?? [];
  },

  getSubscription: async (agencyId: string, details?: BillingAgencyDetails): Promise<SubscriptionData | null> => {
    console.log("[billing] getSubscription", { agencyId, details });
    try {
      const { data } = await billingClient.get<{ response?: SubscriptionData }>("/subscription", {
        headers: agencyHeaders(agencyId, details),
      });
      return data.response ?? null;
    } catch (error: any) {
      if (error?.errors?.length) return null;
      throw error;
    }
  },

  getSubscriptionAccess: async (agencyId: string): Promise<BillingAgencyAccess> => {
    console.log("[billing] getSubscriptionAccess", { agencyId });
    const { data } = await billingClient.get<{ response: BillingAgencyAccess }>("/subscription/access", {
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  getPaymentLink: async (agencyId: string, payload: PaymentLinkPayload): Promise<string> => {
    console.log("[billing] getPaymentLink", { agencyId, payload });
    const { data } = await billingClient.post<{ response: string }>("/payment/link", payload, {
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  upgradeSubscription: async (
    agencyId: string,
    newProductConfigId: number,
    priceId: string,
    promotionCode?: string,
  ): Promise<string> => {
    console.log("[billing] upgradeSubscription", { agencyId, newProductConfigId, priceId, promotionCode });
    const { data } = await billingClient.post<{ response: string }>("/subscription/upgrade", null, {
      params: { productConfigId: newProductConfigId, priceId, promotionCode: promotionCode || undefined },
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  previewUpgrade: async (
    agencyId: string,
    newProductConfigId: number,
    priceId: string,
    promotionCode?: string,
  ): Promise<UpgradePreview> => {
    console.log("[billing] previewUpgrade", { agencyId, newProductConfigId, priceId, promotionCode });
    const { data } = await billingClient.get<{ response: UpgradePreview }>("/subscription/upgrade/preview", {
      params: { productConfigId: newProductConfigId, priceId, promotionCode: promotionCode || undefined },
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  downgradeSubscription: async (
    agencyId: string,
    newProductConfigId: number,
    priceId: string,
  ): Promise<string> => {
    console.log("[billing] downgradeSubscription", { agencyId, newProductConfigId, priceId });
    const { data } = await billingClient.post<{ response: string }>("/subscription/downgrade", null, {
      params: { productConfigId: newProductConfigId, priceId },
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  purchaseAddon: async (agencyId: string, productConfigId: number, priceId: string): Promise<string> => {
    console.log("[billing] purchaseAddon", { agencyId, productConfigId, priceId });
    const { data } = await billingClient.post<{ response: string }>("/subscription/addon", null, {
      params: { productConfigId, priceId },
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  syncSubscription: async (agencyId: string): Promise<unknown> => {
    console.log("[billing] syncSubscription", { agencyId });
    const { data } = await billingClient.post("/admin/sync-subscription", null, {
      params: { agencyId },
    });
    return data;
  },

  createFreemiumSubscription: async (
    branchId: string,
    payload: FreemiumSubscriptionPayload,
  ): Promise<void> => {
    console.log("[billing] createFreemiumSubscription", { branchId, payload });
    await billingClient.post("/subscription/freemium", payload, {
      headers: agencyHeaders(branchId),
    });
  },

  getInvoiceDetails: async (agencyId: string, invoiceId: string): Promise<PaymentTransaction> => {
    console.log("[billing] getInvoiceDetails", { agencyId, invoiceId });
    const { data } = await billingClient.get<{ response: PaymentTransaction }>(`/invoice/${invoiceId}`, {
      headers: agencyHeaders(agencyId),
    });
    return data.response;
  },

  getLatestInvoice: async (agencyId: string): Promise<PaymentTransaction | null> => {
    console.log("[billing] getLatestInvoice", { agencyId });
    const { data } = await billingClient.get<{ response: InvoicePageResponse }>("/invoice", {
      params: { page: 0, pageSize: 5 },
      headers: agencyHeaders(agencyId),
    });
    const invoices =
      data.response?.content ??
      data.response?._embedded?.invoiceDtoList ??
      data.response?._embedded?.invoices ??
      data.response?._embedded?.content ??
      data.response?.page?.content ??
      [];
    return invoices.find((invoice) => Number(invoice.paymentDetails?.total ?? 0) > 0) ?? invoices[0] ?? null;
  },
};
